import { Injectable, OnDestroy } from '@angular/core';

import { Subscription } from 'rxjs';
import { MessageService } from 'primeng/api';
// eslint-disable-next-line @typescript-eslint/naming-convention
import GPT3Tokenizer from 'gpt3-tokenizer';

import { ConfigService } from '../../../Core/Config/services/config.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { DropdownOptionModel } from '../models/dropdown-option.model';
import { AIChatCompletionApiModel, AIChatCompletionMessageApiModel } from '../../../api/ai/model/AI-chat-completion-api.model';
import { ChatMessageTypeModel, ChatRole } from '../../../api/ai/enums/AI-chat-completion.enum';
import { messageSeverityEnum } from '../../Misc/enums/message-severity.enum';
import { TranslationModel } from '../models/translation.model';

@Injectable()
export class TranslateService implements OnDestroy {
    readonly maxTokenLimit = 2000;
    tokenizer = new GPT3Tokenizer({ type: 'gpt3' });

    subscriptions: Subscription[] = [];

    constructor(
        public localizationService: LocalizationService,
        public messageService: MessageService,
        public configService: ConfigService,
    ) { }

    setupChatAIForText(translation: TranslationModel, language: DropdownOptionModel) {
        const tokenSize = this.tokenizer.encode(translation.originalContent || '')?.text?.length;
        const numberOfApiCalls = Math.ceil(tokenSize / this.maxTokenLimit);

        const texts = this.textToArray(translation.originalContent, translation.originalContent.length / numberOfApiCalls);
        const requestModels = this.getRequestModelForText(texts, language);
        translation.translatedContent = '';
        this.fetchDataForText(requestModels, translation, language);
    }

    textToArray(text: string, length: number): string[] {
        const words = text.split(' ');
        const arr: string[] = [];
        let temp: string = '';
        let count = 0;

        for (const word of words) {
            if (count + word.length <= length) {
                temp += word + ' ';
                count += word.length;
            } else {
                arr.push(temp);
                temp = '';
                count = 0;
                temp = word + ' ';
                count += word.length;
            }
        }
        arr.push(temp);
        return arr;
    }

    fetchDataForText(
        requestModels: AIChatCompletionApiModel[],
        translation: TranslationModel,
        language: DropdownOptionModel,
    ) {
        const requestModel = requestModels.shift();

        if (!requestModel) {
            return;
        }

        const uint8array = new TextDecoder();
        fetch(this.configService.getConfig().apiUrl + '/ai/chat/completions', {
            'headers': {
                'accept': 'text/plain',
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'content-type': 'application/json',
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'Ai-Reference-Id': translation.id
            },
            'body': JSON.stringify(requestModel),
            'method': 'POST',
            'mode': 'cors',
            'credentials': 'include'
        }).then(
            (response) => {
                if (response.status !== 200) {
                    // should give us some context as to why the call fails (at least for now)
                    console.log('response', response);
                    throw new Error;
                }
                const reader = response.body.getReader();
                const pump = (controller) => {
                    return reader.read().then(({ done, value }) => {
                        let nextValue = uint8array.decode(value);

                        if (!translation.translatedContent || (translation.translatedContent === '')) {
                            if (nextValue.trim() === '.') {
                                // Handle the case of some amount of whitespace around a .
                                nextValue = '';
                            } else {
                                // handle the case of starting white space but otherwise good content
                                nextValue = nextValue.trimStart();

                                // handle OpenAI bug that returns undesired fence code block wrapper
                                if (nextValue.startsWith('```json')) {
                                    nextValue = nextValue.replace('```json', '');
                                }

                                if (nextValue.startsWith('```html')) {
                                    nextValue = nextValue.replace('```html', '');
                                }

                                if (nextValue.startsWith('html')) {
                                    nextValue = nextValue.replace('html', '');
                                }
                            }
                        }

                        if (nextValue.endsWith('```')) {
                            nextValue = nextValue.replace('```', '');
                        }

                        translation.translatedContent += nextValue;

                        // When no more data needs to be consumed, close the stream
                        if (done) {
                            controller.close();
                            reader?.cancel();
                            if (requestModels.length) {
                                this.fetchDataForText(requestModels, translation, language);
                                return;
                            }

                            translation.setIsTranslating(false);

                            return;
                        } else {
                            translation.setIsTranslating(true);
                        }
                        // Enqueue the next data chunk into our target stream
                        controller.enqueue(value);
                        return pump(controller);
                    });
                };
                const startFunction = (controller) => {
                    return pump(controller);
                };
                return new ReadableStream({
                    start: startFunction
                });
            }
        ).catch(
            error => {
                translation.setIsTranslating(false);
                console.log('error with streaming', error);

                this.messageService.add(
                    {
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('Mention.translate.error.title'),
                        detail: this.localizationService.transform('Mention.translate.error.description')
                    }
                );
            }
        );
    }

    getRequestModelForText(texts: string[], language: DropdownOptionModel): AIChatCompletionApiModel[] {
        const messagesRequest = [];

        texts.forEach(text => {
            const message1: AIChatCompletionMessageApiModel = {
                role: ChatRole.USER,
                chatMessageType: ChatMessageTypeModel.BASIC,
                message : `${text}`
            };

            const message2: AIChatCompletionMessageApiModel = {
                role: ChatRole.USER,
                chatMessageType: ChatMessageTypeModel.BASIC,
                message : `Translate the above content to ${language.value} while keeping html tags and formats.`
            };

            messagesRequest.push({ messages: [message1, message2]} as AIChatCompletionApiModel);
        });

        return messagesRequest;
    }

    ngOnDestroy(): void {
        this.subscriptions?.forEach(sub => sub.unsubscribe());
    }
}
